import React, { useState } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import TextField from 'components/CustomFields';
import { SimpleSelect } from 'components/CustomSelectFields';
import CustomButton from 'components/CustomButton';
import ConfirmationDialog from 'dialogs/ConfirmationDialog';
import useResumeStore from './store';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(4, 6),
    overflowY: 'auto',
    flex: 1,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.neutral['clr-300'],
      borderRadius: '3px',
    },
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    width: 'fit-content',
    padding: theme.spacing(2, 0),
    '&:hover': {
      opacity: 0.8,
    },
  },
  accordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    borderRadius: `${theme.spacing(1)} !important`,
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 !important',
    },
  },
  accordionSummary: {
    minHeight: '56px !important',
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '0 !important',
    },
  },
  summaryLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(0, 4, 4, 4),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
    paddingTop: theme.spacing(4),
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  graduationDateRow: {
    display: 'flex',
    gap: theme.spacing(4),
    flex: 1,
  },
  fieldRowWithButton: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'flex-end',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(4, 6),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    gap: theme.spacing(2),
  },
}));

const degreeOptions = [
  { _id: 'high_school', name: 'High School' },
  { _id: 'associate', name: 'Associate Degree' },
  { _id: 'bachelor', name: "Bachelor's Degree" },
  { _id: 'master', name: "Master's Degree" },
  { _id: 'diploma', name: 'Diploma' },
];

const monthOptions = [
  { _id: 'January', name: 'January' },
  { _id: 'February', name: 'February' },
  { _id: 'March', name: 'March' },
  { _id: 'April', name: 'April' },
  { _id: 'May', name: 'May' },
  { _id: 'June', name: 'June' },
  { _id: 'July', name: 'July' },
  { _id: 'August', name: 'August' },
  { _id: 'September', name: 'September' },
  { _id: 'October', name: 'October' },
  { _id: 'November', name: 'November' },
  { _id: 'December', name: 'December' },
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 30 }, (_, i) => {
  const year = String(currentYear + 5 - i);
  return { _id: year, name: year };
});

const EducationEntry = ({ index, data, expanded, onToggle, onUpdate, onDelete }) => {
  const classes = useStyles();

  const handleChange = (field) => (e) => {
    onUpdate(index, { [field]: e.target.value });
  };

  const handleSelectChange = (field) => (option) => {
    onUpdate(index, { [field]: option._id });
  };

  const title = data.schoolName || `Education ${index + 1}`;
  const degreeName = data.degree
    ? degreeOptions.find((o) => o._id === data.degree)?.name
    : '';
  const subtitle = [degreeName, data.fieldOfStudy].filter(Boolean).join(' — ');

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={() => onToggle(index)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
      >
        <Box className={classes.summaryLeft}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant='body02-medium' color='neutral.clr-500'>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          size='small'
          onClick={(e) => { e.stopPropagation(); onDelete(index); }}
          sx={{ mr: 1 }}
        >
          <DeleteOutlineIcon fontSize='small' color='error' />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Box className={classes.fieldRow}>
          <Box flex={1}>
            <TextField
              label='Name'
              placeholder='School / University Name'
              value={data.schoolName}
              onChange={handleChange('schoolName')}
              required
            />
          </Box>
          <Box flex={1}>
            <TextField
              label='Location'
              placeholder='Delhi, India'
              value={data.schoolLocation}
              onChange={handleChange('schoolLocation')}
              required
            />
          </Box>
        </Box>

        <Box className={classes.fieldRow}>
          <Box flex={1}>
            <SimpleSelect
              label='Degree'
              placeholder='Select'
              options={degreeOptions}
              value={data.degree
                ? degreeOptions.find((o) => o._id === data.degree)
                : null
              }
              onSelect={handleSelectChange('degree')}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box flex={1}>
            <Typography
              sx={{
                fontWeight: 700,
                textTransform: 'capitalize',
                marginBottom: '8px',
                fontSize: '14px',
              }}
            >
              Graduation Date (or expected Graduation Date)
            </Typography>
            <Box className={classes.graduationDateRow}>
              <Box flex={1}>
                <SimpleSelect
                  placeholder='Month'
                  options={monthOptions}
                  value={data.graduationMonth
                    ? monthOptions.find((o) => o._id === data.graduationMonth)
                    : null
                  }
                  onSelect={handleSelectChange('graduationMonth')}
                  sx={{ width: '100%', minWidth: '100px' }}
                />
              </Box>
              <Box flex={1}>
                <SimpleSelect
                  placeholder='Year'
                  options={yearOptions}
                  value={data.graduationYear
                    ? yearOptions.find((o) => o._id === data.graduationYear)
                    : null
                  }
                  onSelect={handleSelectChange('graduationYear')}
                  sx={{ width: '100%', minWidth: '100px' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={classes.fieldRow}>
          <Box flex={1}>
            <TextField
              label='Field of Study'
              placeholder='Financial Accounting'
              value={data.fieldOfStudy}
              onChange={handleChange('fieldOfStudy')}
            />
          </Box>
          <Box flex={1} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const EducationForm = ({ onNext }) => {
  const classes = useStyles();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const [
    educations, addEducation, updateEducation, removeEducation,
  ] = useResumeStore((state) => [
    state.educations, state.addEducation,
    state.updateEducation, state.removeEducation,
  ]);

  const handleAdd = () => {
    addEducation();
    setExpandedIndex(educations.length);
  };

  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleDelete = (index) => {
    setDeleteIndex(index);
  };

  const handleConfirmDelete = () => {
    removeEducation(deleteIndex);
    if (expandedIndex === deleteIndex) {
      setExpandedIndex(null);
    } else if (expandedIndex > deleteIndex) {
      setExpandedIndex(expandedIndex - 1);
    }
    setDeleteIndex(null);
  };

  const isNextDisabled = educations.length === 0 ||
    educations.some((edu) =>
      !edu.schoolName?.trim() ||
      !edu.schoolLocation?.trim()
    );

  return (
    <Box className={classes.container}>
      <Box className={classes.content}>
        {educations.length === 0 && (
          <Box className={classes.emptyState}>
            <Typography variant='body01-medium' color='neutral.clr-400'>
              No education added yet. Click the button below to add one.
            </Typography>
          </Box>
        )}

        {educations.map((entry, index) => (
          <EducationEntry
            key={index}
            index={index}
            data={entry}
            expanded={expandedIndex === index}
            onToggle={handleToggle}
            onUpdate={updateEducation}
            onDelete={handleDelete}
          />
        ))}

        <Box className={classes.addButton} onClick={handleAdd}>
          <AddIcon sx={{ fontSize: '20px', color: 'primary.main' }} />
          <Typography variant='body01-bold' color='primary.main'>
            Add Education
          </Typography>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Typography variant='body02-bold' color='warning.main'>
          Note: Your data will be lost if you leave this page.
        </Typography>
        <CustomButton
          size='medium'
          onClick={onNext}
          disabled={isNextDisabled}
          sx={{ width: 'fit-content' }}
        >
          Next: Skills
        </CustomButton>
      </Box>

      <ConfirmationDialog
        open={deleteIndex !== null}
        title='Delete Education'
        message='Are you sure you want to delete this education?'
        primaryAction='Delete'
        secondaryAction='Cancel'
        onPrimaryAction={handleConfirmDelete}
        onSecondaryAction={() => setDeleteIndex(null)}
      />
    </Box>
  );
};

export default EducationForm;
